import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { TrendingUp } from "lucide-react";

/**
 * EmailTrendChart — Checkpoint 3.2 "Performance Over Time" section.
 *
 * A small multi-metric sparkline grid: each of the five acquisition
 * metrics gets its own independently-scaled line, since Delivered (~5,000)
 * and High Intent Actions (~10) would otherwise render on incompatible
 * scales on a single shared axis.
 *
 * Reimplements the lightweight, dependency-free hand-rolled SVG technique
 * already established by TrendCharts.jsx's MiniLineChart, rather than
 * importing/modifying that file (MiniLineChart is private/unexported
 * there) or introducing a charting library.
 */
const METRICS = [
  { key: "delivered", label: "Delivered", color: "#6366f1" },
  { key: "landing_page_views", label: "LP Views", color: "#8b5cf6" },
  { key: "quote_starts", label: "Quote Starts", color: "#06b6d4" },
  { key: "quote_completes", label: "Quote Completes", color: "#10b981" },
  { key: "high_intent_actions", label: "High Intent", color: "#f59e0b" },
];

const CHART_WIDTH = 160;
const CHART_HEIGHT = 48;
const PADDING = 4;

function Sparkline({ points, color }) {
  if (!points || points.length === 0) {
    return (
      <div className="flex h-[48px] items-center justify-center text-xs text-slate-500">No data</div>
    );
  }

  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const xStep = (CHART_WIDTH - PADDING * 2) / Math.max(points.length - 1, 1);

  const path = points
    .map((v, i) => {
      const x = PADDING + i * xStep;
      const y = PADDING + (CHART_HEIGHT - PADDING * 2) * (1 - (v - min) / range);
      return `${i === 0 ? "M" : "L"}${x},${y}`;
    })
    .join(" ");

  return (
    <svg
      width={CHART_WIDTH}
      height={CHART_HEIGHT}
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      data-testid="email-trend-sparkline"
    >
      <path d={path} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// DASH-EMAIL-010: previously `return null` when trend was empty — that was
// unreachable in practice, since the adapter always fabricated points. Now
// that the adapter honestly returns an empty array, this renders an
// explicit unavailable state instead of silently disappearing, so the user
// understands why no chart appears rather than concluding nothing changed.
// The outer Card keeps the SAME data-testid ("email-trend-chart") in both
// branches — presence of the section is unchanged; only its content
// reflects whether real trend data exists.
export default function EmailTrendChart({ trend }) {
  const hasTrend = trend && trend.length > 0;

  return (
    <Card className="glass border-white/10" data-testid="email-trend-chart">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <TrendingUp className={`h-5 w-5 ${hasTrend ? "text-indigo-400" : "text-slate-500"}`} />
          Performance Over Time
        </CardTitle>
      </CardHeader>
      <CardContent>
        {hasTrend ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {METRICS.map((m) => {
              const points = trend.map((t) => t[m.key] || 0);
              const latest = points[points.length - 1];
              return (
                <div key={m.key} className="space-y-1" data-testid={`email-trend-metric-${m.key}`}>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-300/70">{m.label}</p>
                  <Sparkline points={points} color={m.color} />
                  <p className="text-sm font-semibold text-slate-100">{latest.toLocaleString()}</p>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-400" data-testid="email-trend-chart-unavailable">
            Trend data is unavailable — this time series is not yet backed by real event-time/send-cohort data
            (EMAIL-ANALYTICS-SPEC-001 §19).
          </p>
        )}
      </CardContent>
    </Card>
  );
}
